// Admin API service for connecting to the backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

class AdminApiError extends Error {
  constructor(message: string, public status?: number) {
    super(message)
    this.name = 'AdminApiError'
  }
}

interface LoginCredentials {
  email: string
  password: string
}

interface LoginResponse {
  success: boolean
  token: string
  user: {
    id: string
    email: string
    name: string
    role: string
    permissions?: any
    avatar?: string
  }
}

interface UserData {
  id: string
  email: string
  name: string
  role: 'USER' | 'ADMIN' | 'SUPER_ADMIN'
  company?: string
  jobTitle?: string
  phone?: string
  avatar?: string
  isActive: boolean
  emailVerified: boolean
  createdAt: string
  updatedAt?: string
  lastLoginAt?: string
}

interface CreateUserData {
  email: string
  name: string
  role?: 'USER' | 'ADMIN'
  company?: string
  jobTitle?: string
  phone?: string
}

interface ChangePasswordData {
  currentPassword: string
  newPassword: string
}

interface DashboardStats {
  totalDemos: number
  activeDemos: number
  totalBookings: number
  pendingBookings: number
  totalUsers: number
  totalProducts: number
}

interface RecentBooking {
  id: string
  demoName: string
  customerName: string
  company?: string
  contactEmail: string
  startTime: string
  status: string
  attendeeCount: number
}

interface UpcomingDemo {
  id: string
  name: string
  startTime: string
  attendeeCount: number
  status: string
}

class AdminApiService {
  private token: string | null = null

  constructor() {
    // Get token from localStorage
    this.token = localStorage.getItem('admin_token')
  }

  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    }
    
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }
    
    return headers
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers
      }
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new AdminApiError(
          errorData.error || errorData.message || `HTTP ${response.status}`,
          response.status
        )
      }

      const data = await response.json()
      
      if (!data.success && data.error) {
        throw new AdminApiError(data.error)
      }
      
      return data
    } catch (error) {
      if (error instanceof AdminApiError) {
        throw error
      }
      throw new AdminApiError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const data = await this.makeRequest<LoginResponse>('/admin?action=login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    })
    
    if (data.token) {
      this.token = data.token
      localStorage.setItem('admin_token', data.token)
    }
    
    return data
  }

  async logout(): Promise<void> {
    await this.makeRequest('/admin?action=logout', {
      method: 'POST'
    })
    
    this.token = null
    localStorage.removeItem('admin_token')
  }

  async getSession(): Promise<{ user: UserData }> {
    return this.makeRequest('/admin?action=session')
  }

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.makeRequest<{ data: DashboardStats }>('/admin?action=dashboard')
    return response.data
  }

  async getRecentBookings(): Promise<RecentBooking[]> {
    const data = await this.makeRequest<{ data: { recentBookings: RecentBooking[] } }>('/admin?action=dashboard')
    return data.data.recentBookings || []
  }

  async getUpcomingDemos(): Promise<UpcomingDemo[]> {
    const data = await this.makeRequest<{ data: { upcomingDemos: UpcomingDemo[] } }>('/admin?action=dashboard')
    return data.data.upcomingDemos || []
  }

  // User Management
  async getUsers(): Promise<UserData[]> {
    const response = await this.makeRequest<{ data: UserData[] }>('/admin?action=users')
    return response.data || []
  }

  async getUser(userId: string): Promise<UserData> {
    const response = await this.makeRequest<{ data: UserData }>(`/admin?action=get-user&userId=${userId}`)
    return response.data
  }

  async createUser(userData: CreateUserData): Promise<UserData & { temporaryPassword: string }> {
    const response = await this.makeRequest<{ data: UserData & { temporaryPassword: string } }>('/admin?action=create-user', {
      method: 'POST',
      body: JSON.stringify(userData)
    })
    return response.data
  }

  async updateUser(userId: string, userData: Partial<UserData>): Promise<UserData> {
    const response = await this.makeRequest<{ data: UserData }>(`/admin?action=update-user&userId=${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    })
    return response.data
  }

  async deleteUser(userId: string): Promise<void> {
    return this.makeRequest(`/admin?action=delete-user&userId=${userId}`, {
      method: 'DELETE'
    })
  }

  async changePassword(passwordData: ChangePasswordData): Promise<void> {
    return this.makeRequest('/admin?action=change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData)
    })
  }

  async uploadAvatar(file: File): Promise<UserData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = async () => {
        try {
          const response = await this.makeRequest<{ data: UserData }>('/admin?action=upload-avatar', {
            method: 'POST',
            body: JSON.stringify({
              filename: file.name,
              fileData: reader.result
            })
          })
          resolve(response.data)
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }

  async getAuditLogs(params?: { page?: number; limit?: number }): Promise<{
    data: any[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    const queryString = new URLSearchParams({
      page: String(params?.page || 1),
      limit: String(params?.limit || 50)
    }).toString()

    return this.makeRequest(`/admin?action=audit-logs&${queryString}`, {
      method: 'GET'
    })
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.token
  }

  setToken(token: string): void {
    this.token = token
    localStorage.setItem('admin_token', token)
  }

  clearAuth(): void {
    this.token = null
    localStorage.removeItem('admin_token')
  }
}

// Export singleton instance
export const adminApi = new AdminApiService()
export { AdminApiError }
export type {
  LoginCredentials,
  LoginResponse,
  UserData,
  CreateUserData,
  ChangePasswordData,
  DashboardStats,
  RecentBooking,
  UpcomingDemo
}